import React, { useState } from 'react';
import { toast } from 'react-toastify';
import useOrganizationService from '../../../../app/services/hooks/useOrganizationService';
import useHandleErrorFields from '../../../../hooks/useHandleErrorFields';
import useReactConfirmAlert from '../../../../hooks/useReactConfirmAlert';
import useRefreshAuth from '../../../../hooks/useRefreshAuth';
import DefaultCard from '../../../../components/default/DefaultCard';
import DefaultModal from '../../../../components/default/DefaultModal';
import ButtonSaveForm from '../../../../components/buttons/ButtonSaveForm';
import ButtonGeneral from '../../../../components/buttons/ButtonGeneral';
import InputField from '../../../../components/InputField';
import TextAreaField from '../../../../components/TextAreaField';
import { formatDate } from '../../types';
import { OrganizationContextProps } from '../index';

const DataTab: React.FC<OrganizationContextProps> = ({ organization, membership, reload }) => {
    const {
        updateMyOrganization, fetchingUpdateOrganization, exportOrganizationData, fetchingExport,
        closeOrganization, cancelOrganizationClosure, fetchingClosure
    } = useOrganizationService();
    const { fieldErrorMessage, onFocusRemove, setErrors } = useHandleErrorFields();
    const { requestConfirmation } = useReactConfirmAlert();
    const { refreshAuth } = useRefreshAuth();
    const [form, setForm] = useState({
        name: organization.name,
        rut: organization.rut,
        email: organization.email,
        phone: organization.phone,
        address: organization.address
    });
    const [showClose, setShowClose] = useState(false);
    const [closeForm, setCloseForm] = useState({ confirm_name: '', reason: '' });

    const canEdit = membership.is_admin && organization.status === 'active';
    const isFirm = organization.organization_type === 'firm';
    const entity = isFirm ? 'el estudio' : 'la cuenta';
    const isClosed = organization.status === 'closed';
    // La baja solo la puede pedir el dueño (el backend lo valida igual)
    const canClose = membership.is_admin && membership.is_owner && organization.status === 'active';
    const set = (field: keyof typeof form) => (value: string) => setForm({ ...form, [field]: value });

    const handleSave = () => {
        updateMyOrganization(form, {
            onSuccess: (response) => {
                toast.success(response.message);
                setErrors({});
                reload();
            },
            onError: (response) => toast.error(response.message),
            onFieldError: (response) => {
                setErrors(response.data);
                toast.error('Revisa los campos marcados');
            }
        });
    };

    const handleExport = () => {
        exportOrganizationData({
            onSuccess: () => toast.success('Descarga iniciada.'),
            onError: (message) => toast.error(message)
        });
    };

    const handleClose = () => {
        closeOrganization(closeForm, {
            onSuccess: (response) => {
                toast.success(response.message);
                setShowClose(false);
                setCloseForm({ confirm_name: '', reason: '' });
                setErrors({});
                reload();
                refreshAuth();
            },
            onError: (response) => toast.error(response.message),
            onFieldError: (response) => {
                setErrors(response.data);
                toast.error(response.data?.confirm_name?.[0] || 'Revisa los datos');
            }
        });
    };

    const handleCancelClosure = () => {
        requestConfirmation({
            title: '¿Revertir la baja?',
            message: `${isFirm ? 'El estudio vuelve a quedar activo' : 'La cuenta vuelve a quedar activa'} con el plan gratuito y sus datos no se eliminan. Si quieres un plan pagado, contrátalo de nuevo en Suscripción.`,
            buttons: {
                confirmButton: {
                    label: 'Sí, revertir',
                    onClick: () =>
                        cancelOrganizationClosure({
                            onSuccess: (response) => {
                                toast.success(response.message);
                                reload();
                                refreshAuth();
                            },
                            onError: (response) => toast.error(response.message)
                        })
                },
                cancelButton: { label: 'Volver' }
            }
        });
    };

    return (
        <>
            <DefaultCard>
                <h5 className="mb-3">Datos {isFirm ? 'del estudio' : 'profesionales'}</h5>
                <div className="row">
                    <div className="col-md-6 mb-3">
                        <InputField label="Nombre" value={form.name} disabled={!canEdit} required onChange={set('name')}
                                    onFocus={() => onFocusRemove('name')} error={fieldErrorMessage('name')} />
                    </div>
                    <div className="col-md-6 mb-3">
                        <InputField label="RUT" value={form.rut} disabled={!canEdit} onChange={set('rut')}
                                    onFocus={() => onFocusRemove('rut')} error={fieldErrorMessage('rut')} />
                    </div>
                    <div className="col-md-6 mb-3">
                        <InputField label="Correo de contacto" type="email" value={form.email} disabled={!canEdit} onChange={set('email')}
                                    onFocus={() => onFocusRemove('email')} error={fieldErrorMessage('email')} />
                    </div>
                    <div className="col-md-6 mb-3">
                        <InputField label="Teléfono" value={form.phone} disabled={!canEdit} onChange={set('phone')}
                                    onFocus={() => onFocusRemove('phone')} error={fieldErrorMessage('phone')} />
                    </div>
                    <div className="col-12 mb-3">
                        <InputField label="Dirección" value={form.address} disabled={!canEdit} onChange={set('address')}
                                    onFocus={() => onFocusRemove('address')} error={fieldErrorMessage('address')} />
                    </div>
                </div>
                <p className="text-muted small">Identificador: <code>{organization.slug}</code> · Dueño: {organization.owner_name} ({organization.owner_email})</p>
                {canEdit && (
                    <div className="d-flex justify-content-end">
                        <ButtonSaveForm callbackSave={handleSave} loading={fetchingUpdateOrganization} />
                    </div>
                )}
            </DefaultCard>

            <DefaultCard>
                <h5 className="mb-2">Contrato de encargo de tratamiento de datos</h5>
                {organization.dpa_accepted_at ? (
                    <p className="mb-0 small" data-testid="dpa-status">
                        <i className={`fas ${organization.dpa_accepted ? 'fa-check-circle text-success' : 'fa-exclamation-circle text-warning'} me-2`} />
                        Versión {organization.dpa_version || '—'} aceptada el {formatDate(organization.dpa_accepted_at)}.
                        {!organization.dpa_accepted && ' Hay una versión nueva pendiente de aceptar.'}{' '}
                        <a href="/contrato-encargo" target="_blank" rel="noopener noreferrer">Ver contrato</a>
                    </p>
                ) : (
                    <p className="mb-0 small text-muted" data-testid="dpa-status">
                        Aún no se acepta el contrato de encargo.{' '}
                        <a href="/contrato-encargo" target="_blank" rel="noopener noreferrer">Ver contrato</a>
                        {membership.is_admin && organization.status !== 'closed' && ' · Puedes aceptarlo desde el aviso en la parte superior.'}
                    </p>
                )}
            </DefaultCard>

            {membership.is_admin && (
                <DefaultCard>
                    <h5 className="mb-2">Tus datos y salida de Monere</h5>
                    <p className="text-muted small">
                        Descarga en un ZIP los clientes, casos, contratos y documentos de {entity}. Puedes hacerlo cuando quieras.
                    </p>
                    <ButtonGeneral onClick={handleExport} title="Descargar mis datos (ZIP)" icon="fa-file-archive" size="small"
                                   variant="outline" loading={fetchingExport} />

                    <hr />
                    {isClosed ? (
                        <div className="alert alert-danger mb-0 d-flex flex-wrap justify-content-between align-items-center gap-2" data-testid="closure-status">
                            <span>
                                {isFirm ? 'El estudio se dio de baja' : 'La cuenta se dio de baja'} el {formatDate(organization.closed_at)}.
                                Sus datos se eliminarán definitivamente el <strong>{formatDate(organization.purge_after)}</strong>.
                                Descárgalos antes si los necesitas.
                            </span>
                            <ButtonGeneral onClick={handleCancelClosure} title="Revertir baja" icon="fa-undo" size="small"
                                           loading={fetchingClosure} />
                        </div>
                    ) : (
                        <div className="d-flex flex-wrap justify-content-between align-items-center gap-2">
                            <div>
                                <h6 className="mb-1 text-danger">Dar de baja {entity}</h6>
                                <small className="text-muted">
                                    {membership.is_owner
                                        ? 'Cancela la suscripción y los datos se eliminan 30 días después.'
                                        : 'Solo el dueño puede dar de baja la cuenta.'}
                                </small>
                            </div>
                            {canClose && (
                                <ButtonGeneral
                                    onClick={() => {
                                        setErrors({});
                                        setShowClose(true);
                                    }}
                                    title={`Dar de baja ${entity}`}
                                    icon="fa-door-open"
                                    size="small"
                                    variant="danger"
                                />
                            )}
                        </div>
                    )}
                </DefaultCard>
            )}

            <DefaultModal
                title={`Dar de baja ${entity}`}
                show={showClose}
                handleClose={() => setShowClose(false)}
                actionButton={
                    <ButtonGeneral onClick={handleClose} title="Dar de baja" variant="danger" icon="fa-door-open"
                                   loading={fetchingClosure} disabled={!closeForm.confirm_name.trim()} />
                }
            >
                <div className="alert alert-warning small">
                    <ul className="mb-0 ps-3">
                        <li>Se cancela la suscripción de inmediato, <strong>sin reembolso</strong> del período en curso.</li>
                        <li>{isFirm ? 'El estudio queda' : 'La cuenta queda'} en <strong>solo lectura</strong>: nadie podrá hacer cambios.</li>
                        <li>Los datos (clientes, casos, contratos y documentos) se <strong>eliminan 30 días después</strong>. Mientras tanto puedes revertir la baja.</li>
                    </ul>
                </div>
                <p className="small">
                    Te recomendamos{' '}
                    <button type="button" className="btn btn-link btn-sm p-0 align-baseline" onClick={handleExport} disabled={fetchingExport}>
                        descargar tus datos (ZIP)
                    </button>{' '}
                    antes de continuar.
                </p>
                <div className="mb-3">
                    <InputField label={`Escribe "${organization.name}" para confirmar`} value={closeForm.confirm_name} required
                                onChange={(value) => setCloseForm({ ...closeForm, confirm_name: value })}
                                onFocus={() => onFocusRemove('confirm_name')} error={fieldErrorMessage('confirm_name')} />
                </div>
                <TextAreaField label="Motivo (opcional)" value={closeForm.reason} rows={3}
                               onChange={(value) => setCloseForm({ ...closeForm, reason: value })} />
            </DefaultModal>
        </>
    );
};

export default DataTab;
